import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Smartphone } from "lucide-react";
import { Button, Section, When, useAuth, useToast } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";

// Set up a phone by scanning a code (Oz, 2026-11-03): the mailbox app, installed and signed in with one
// scan and no password typed. The code is made on THIS signed-in screen, works once, for five minutes, and
// the phone it signs in is a device of its own — shown here the moment it is used, with a way to sign
// exactly that device out if it was not who you expected (internal/auth/pairing.go).
//
// One component for both askers: the mailbox's own Security page (/auth/pair) and an account
// administrator setting up someone's phone (/mail/domains/{domain}/mailboxes/{localpart}/pair). They
// differ only in the path and in where "sign it out" goes.

interface Issued {
  mailbox: string;
  url: string;
  qr?: string;
  qr_error?: string;
  expires_at: string;
  lifetime_seconds: number;
}

interface PairState {
  state: "none" | "waiting" | "expired" | "paired";
  expires_at?: string;
  paired_at?: string;
  session_id?: string;
  device_name?: string;
  ip?: string;
  signed_in: boolean;
}

export interface PhoneSetupProps {
  /** POST makes a code; GET reads whether it was used. */
  path: string;
  /** Where one session of this login is signed out: `${sessionsPath}/${id}` (DELETE). */
  sessionsPath: string;
  /** Who the phone will be signed in as, for the wording. */
  who?: string;
  title?: string;
}

function secondsLeft(iso: string | undefined, now: number): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.parse(iso) - now) / 1000));
}

export function PhoneSetup({ path, sessionsPath, who, title = "Set up on your phone" }: PhoneSetupProps) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [issued, setIssued] = React.useState<Issued | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());

  // Asked only while a code is on screen: every two seconds until it is used or runs out, then not at all.
  const status = useQuery({
    queryKey: ["pair-status", path],
    enabled: !!issued,
    queryFn: () => client.apiFetch<PairState>(path),
    refetchInterval: (q) => (q.state.data?.state === "waiting" || !q.state.data ? 2000 : false),
  });
  const st = status.data;
  const waiting = !!issued && (!st || st.state === "waiting");

  React.useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [waiting]);
  const left = secondsLeft(issued?.expires_at, now);

  React.useEffect(() => {
    if (st?.state === "paired") void qc.invalidateQueries({ queryKey: ["sessions"] });
    if (st?.state === "paired") void qc.invalidateQueries({ queryKey: ["my-sessions"] });
  }, [st?.state, qc]);

  const makeCode = async () => {
    setBusy(true);
    try {
      const out = await client.apiFetch<Issued>(path, { method: "POST" });
      setIssued(out);
      setNow(Date.now());
      void qc.invalidateQueries({ queryKey: ["pair-status", path] });
    } catch (e) {
      notify({ kind: "error", title: "Could not make a code", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    if (!st?.session_id) return;
    try {
      await client.apiFetch(`${sessionsPath}/${encodeURIComponent(st.session_id)}`, { method: "DELETE" });
      notify({ kind: "success", title: "That phone is signed out" });
      void status.refetch();
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      void qc.invalidateQueries({ queryKey: ["my-sessions"] });
    } catch (e) {
      notify({ kind: "error", title: "Could not sign it out", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const expired = !!issued && (st?.state === "expired" || (waiting && left === 0));
  const paired = !!issued && st?.state === "paired";

  return (
    <Section title={title}>
      {!issued && (
        <div className="flex flex-wrap items-center gap-4">
          <p className="max-w-prose text-[13px] leading-relaxed text-muted">
            Scan a code with the phone's camera and the mailbox app opens on it, signed in{who ? ` as ${who}` : ""} — no
            password to type. Then it offers to install itself on the home screen.
          </p>
          <Button variant="accent" onClick={() => void makeCode()} disabled={busy}>
            <Smartphone size={16} aria-hidden /> {busy ? "Making a code…" : "Show a code"}
          </Button>
        </div>
      )}

      {issued && !paired && !expired && (
        <div className="flex flex-col items-start gap-4 sm:flex-row">
          {issued.qr ? (
            <img
              src={issued.qr}
              alt="Scan this code with your phone's camera"
              width={220}
              height={220}
              className="rounded-md border border-line bg-white p-2"
            />
          ) : (
            <p className="text-[13px] text-warn">{issued.qr_error ?? "The code could not be drawn."}</p>
          )}
          <div className="space-y-3 text-[13px] leading-relaxed">
            <ol className="list-decimal space-y-1 pl-5">
              <li>Open the camera on the phone and point it at the code.</li>
              <li>Tap the link that appears. The mailbox opens, signed in.</li>
              <li>Follow its prompt to add the app to the home screen.</li>
            </ol>
            <div className="flex items-center gap-2">
              <StatusPill tone={left <= 30 ? "warn" : "neutral"}>
                works for {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
              </StatusPill>
              <span className="text-[12px] text-muted">once only — waiting for the phone…</span>
            </div>
            <p className="text-[12px] text-muted">
              Anyone who scans it in that time is signed in, so show it only to the phone you mean.
            </p>
            <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {expired && (
        <div className="flex flex-wrap items-center gap-3 text-[13px]">
          <span className="text-muted">That code ran out without being used, so it can no longer sign anything in.</span>
          <Button size="sm" variant="accent" onClick={() => void makeCode()} disabled={busy}>
            New code
          </Button>
        </div>
      )}

      {paired && st && (
        <div className="space-y-3 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={st.signed_in ? "ok" : "neutral"}>{st.signed_in ? "paired" : "signed out"}</StatusPill>
            <span className="font-medium">{st.device_name || "A device"}</span>
            {st.paired_at && (
              <span className="text-muted">
                · <When value={st.paired_at} />
              </span>
            )}
            {st.ip && <span className="font-mono text-[12px] text-faint">{st.ip}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {st.signed_in && (
              <Button size="sm" variant="ghost" onClick={() => void signOut()}>
                Not me? Sign it out
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => void makeCode()} disabled={busy}>
              Set up another phone
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}
